import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { select, Store } from '@ngrx/store';
import { getInitFlow } from '@store/flow-data/init-flow';
import { Observable, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  protected readonly store: Store;
  private readonly http: HttpClient;

  constructor() {
    this.store = inject(Store);
    this.http = inject(HttpClient);
  }

  private getHeaders({ headers, authenticationType }): Observable<HttpHeaders> {
    return of({
      ...headers,
      Accept: 'application/json',
    });
  }

  private toHttpParameters<T>(parameters: T): HttpOutputParametersType {
    return Object.entries(parameters || {}).reduce(
      (accumulator: HttpOutputParametersType, [key, value]: [string, T[keyof T]]) => {
        if (!value) {
          return accumulator;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        accumulator[key] = Array.isArray(value) ? value.filter(Boolean).map((v: any) => v.toSring()) : value.toString();
        return accumulator;
      },
      {} as HttpOutputParametersType
    );
  }

  get<T>(data: ApiRequestPayload<void>): Observable<T>;
  get<T>(data: ApiRequestPayload<void>, requestType: 'response'): Observable<HttpResponse<T>>;
  get<T>(
    {
      apiUrl = environment.apiUrl,
      suffix,
      headers,
      authenticationType = 'basic',
      queryParams,
    }: ApiRequestPayload<void>,
    requestType: RequestType = 'body'
  ): Observable<T | HttpResponse<T>> {
    if (requestType === 'body') {
      return this.makeResponseRequest<void, T>(
        {
          apiUrl,
          suffix,
          headers,
          authenticationType,
          queryParams,
        },
        HttpMethodEnum.GET
      );
    }
    if (requestType === 'response') {
      return this.makeHttpResponseRequest<void, T>(
        {
          apiUrl,
          suffix,
          headers,
          authenticationType,
          queryParams,
        },
        HttpMethodEnum.GET
      );
    }
  }

  post<T, U>(data: ApiRequestPayload<T>): Observable<U>;
  post<T, U>(data: ApiRequestPayload<T>, requestType: 'response'): Observable<HttpResponse<U>>;
  post<T, U>(
    {
      apiUrl = environment.apiUrl,
      suffix,
      headers,
      authenticationType = 'basic',
      queryParams,
      body,
    }: ApiRequestPayload<T>,
    requestType: RequestType = 'body'
  ): Observable<U | HttpResponse<U>> {
    if (requestType === 'body') {
      return this.makeResponseRequest<T, U>(
        {
          apiUrl,
          suffix,
          headers,
          authenticationType,
          body,
          queryParams,
        },
        HttpMethodEnum.POST
      );
    } else if (requestType === 'response') {
      return this.makeHttpResponseRequest<T, U>(
        {
          apiUrl,
          suffix,
          headers,
          authenticationType,
          body,
          queryParams,
        },
        HttpMethodEnum.POST
      );
    }
  }

  put<T, U>(data: ApiRequestPayload<T>): Observable<U>;
  put<T, U>(data: ApiRequestPayload<T>, requestType: 'response'): Observable<HttpResponse<U>>;
  put<T, U>(
    {
      apiUrl = environment.apiUrl,
      suffix,
      headers,
      authenticationType = 'basic',
      queryParams,
      body,
    }: ApiRequestPayload<T>,
    requestType: RequestType = 'body'
  ): Observable<U | HttpResponse<U>> {
    if (requestType === 'body') {
      return this.makeResponseRequest<T, U>(
        {
          apiUrl,
          suffix,
          headers,
          authenticationType,
          body,
          queryParams,
        },
        HttpMethodEnum.PUT
      );
    }
    if (requestType === 'response') {
      return this.makeHttpResponseRequest<T, U>(
        {
          apiUrl,
          suffix,
          headers,
          authenticationType,
          body,
          queryParams,
        },
        HttpMethodEnum.PUT
      );
    }
  }

  delete<T>(data: ApiRequestPayload<void>): Observable<T>;
  delete<T>(data: ApiRequestPayload<void>, requestType: 'response'): Observable<HttpResponse<T>>;
  delete<T>(
    {
      apiUrl = environment.apiUrl,
      suffix,
      headers,
      authenticationType = 'basic',
      queryParams,
    }: ApiRequestPayload<void>,
    requestType: RequestType = 'body'
  ): Observable<T | HttpResponse<T>> {
    if (requestType === 'body') {
      return this.makeResponseRequest<void, T>(
        {
          apiUrl,
          suffix,
          headers,
          authenticationType,
          queryParams,
        },
        HttpMethodEnum.DELETE
      );
    }
    if (requestType === 'response') {
      return this.makeHttpResponseRequest<void, T>(
        {
          apiUrl,
          suffix,
          headers,
          authenticationType,
          queryParams,
        },
        HttpMethodEnum.DELETE
      );
    }
  }

  private makeResponseRequest<T, U>(
    { apiUrl, suffix, headers, authenticationType, queryParams, body }: ApiRequestPayload<T>,
    method: HttpMethodEnum
  ): Observable<U> {
    return this.getHeaders({ headers, authenticationType }).pipe(
      switchMap((headers: HttpHeaders) => {
        if (method === HttpMethodEnum.GET) {
          return this.http.get<U>(`${apiUrl}${suffix}`, {
            params: this.toHttpParameters(queryParams),
            headers,
            observe: 'body',
          });
        }
        if (method === HttpMethodEnum.POST) {
          return this.http.post<U>(`${apiUrl}${suffix}`, body ?? {}, {
            params: this.toHttpParameters(queryParams),
            headers,
            observe: 'body',
          });
        }
        if (method === HttpMethodEnum.PUT) {
          return this.http.put<U>(`${apiUrl}${suffix}`, body ?? {}, {
            params: this.toHttpParameters(queryParams),
            headers,
            observe: 'body',
          });
        }
        if (method === HttpMethodEnum.DELETE) {
          return this.http.delete<U>(`${apiUrl}${suffix}`, {
            params: this.toHttpParameters(queryParams),
            headers,
            observe: 'body',
          });
        }
      })
    );
  }

  private makeHttpResponseRequest<T, U>(
    { apiUrl, suffix, headers, authenticationType, queryParams, body }: ApiRequestPayload<T>,
    method: HttpMethodEnum
  ): Observable<HttpResponse<U>> {
    return this.getHeaders({ headers, authenticationType }).pipe(
      switchMap((headers: HttpHeaders) => {
        if (method === HttpMethodEnum.GET) {
          return this.http.get<U>(`${apiUrl}${suffix}`, {
            params: this.toHttpParameters(queryParams),
            headers,
            observe: 'response',
          });
        }
        if (method === HttpMethodEnum.POST) {
          return this.http.post<U>(`${apiUrl}${suffix}`, body ?? {}, {
            params: this.toHttpParameters(queryParams),
            headers,
            observe: 'response',
          });
        }
        if (method === HttpMethodEnum.PUT) {
          return this.http.put<U>(`${apiUrl}${suffix}`, body ?? {}, {
            params: this.toHttpParameters(queryParams),
            headers,
            observe: 'response',
          });
        }
        if (method === HttpMethodEnum.DELETE) {
          return this.http.delete<U>(`${apiUrl}${suffix}`, {
            params: this.toHttpParameters(queryParams),
            headers,
            observe: 'response',
          });
        }
      })
    );
  }
}

interface ApiRequestPayload<U> extends HeadersPayload {
  apiUrl?: string;
  suffix: string;
  queryParams?: HttpInputParametersType;
  body?: U;
}

interface HeadersPayload {
  headers?: DefaultHttpHeaders;
  authenticationType?: AuthenticationType;
}

export interface DefaultHttpHeaders {
  [HeadersEnum.ACCEPT_LANGUAGE]?: AcceptLanguageEnum;
  [HeadersEnum.X_SESSION_ID]?: string;
  [HeadersEnum.X_API_KEY]?: string;
  [HeadersEnum.X_CORRELATION_ID]?: string;
  [HeadersEnum.GWCOOKIE]?: string;
  [HeadersEnum.IDENTIFIER]?: string;
  [HeadersEnum.CONTENT_TYPE]?: string;
}

type AcceptLanguageEnum = string;
type PrimitiveType = string | number | boolean;
export type HttpInputParametersType = Record<string, PrimitiveType | PrimitiveType[]>;
type HttpOutputParametersType = Record<string, string | string[]>;
type AuthenticationType = 'basic' | 'jwt' | 'onetrust' | 'none';
type RequestType = 'body' | 'response';
enum RequestTypeEnum {
  BODY = 'body',
  RESPONSE = 'response',
}
enum HttpMethodEnum {
  GET = 'get',
  POST = 'post',
  PUT = 'put',
  DELETE = 'delete',
}
export enum HeadersEnum {
  ACCEPT_LANGUAGE = 'Accept-Language',
  CONTENT_TYPE = 'Content-Type',
  X_SESSION_ID = 'x-session-id',
  X_API_KEY = 'x-api-key',
  AUTHORIZATION = 'Authorization',
  GWCOOKIE = 'GWCOOKIE',
  IDENTIFIER = 'Identifier',
  X_CORRELATION_ID = 'x-correlation-id',
}
